import { forwardRef, useCallback, useEffect, useState } from 'react';

import { cn } from '~/lib/cn';

import type { DropdownProps } from '../Dropdown';
import { Dropdown } from '../Dropdown';
import { Input } from '../Input/Input';
import { assertMultiSelect } from './assertions/multiSelect';
import { assertSingleSelect } from './assertions/singleSelect';
import { SEARCH_BY_LABEL, useSearch } from './hooks/useSearch';
import type { ComboboxOption } from './types/ComboboxOption';
import { extendOptionsWithValues } from './utils/extendOptionsWithValues';

export interface ComboboxProps<
  TValue extends string,
  TMultiselect extends boolean | undefined,
> {
  options: ComboboxOption<TValue>[];
  multiselect?: TMultiselect;

  /**
   * If value contains both value and label, they will be added to the options list
   */
  value?: TMultiselect extends true
    ? ComboboxOption<TValue>[]
    : ComboboxOption<TValue>;

  /**
   * Callback for value change
   */
  onChange?: (
    selected: TMultiselect extends true
      ? ComboboxOption<TValue>[]
      : ComboboxOption<TValue>,
  ) => void;

  /**
   * If true, the options will be extended with the value
   */
  extendOptions?: boolean;

  search?: string;
  onSearchChange?: (search: string) => void;

  /**
   * If true, the search will be performed on the client side
   * @default true
   */
  clientSearch?: boolean;

  /**
   * Predicate to filter options on the client side
   * @param option
   * @param search
   * @returns
   */
  searchPredicate?: (option: ComboboxOption, search: string) => boolean;

  /**
   * Placeholder for the options list
   */
  placeholder?: string;

  /**
   * Placeholder for the search input
   */
  searchPlaceholder?: string;

  /**
   * If true, the selected options will be displayed in the dropdown
   */
  displaySelectedOptions?: TMultiselect extends true ? boolean : never;

  /**
   * Name of the input
   */
  name?: string;

  /**
   * Label for the input
   */
  label?: string;

  state?: DropdownProps<TValue, TMultiselect>['state'];

  /**
   * If true, only specified number of options can be selected
   */
  maxSelectedOptions?: number;

  disabled?: boolean;

  /**
   * Text to show when there are no items in the dropdown
   */
  noOptionsText?: string;

  /**
   * Loading state, useful for async options loading
   * @default false
   */
  loading?: boolean;

  /**
   * Called when the user scrolls to the bottom of the dropdown
   * Useful for implementing infinite scroll or loading more options
   * @default undefined
   */
  onLoadMore?: () => Promise<void>;

  /**
   * Distance in pixels from the bottom of the dropdown to trigger `onLoadMore`
   * @default 100
   */
  loadMoreDistance?: number;

  /**
   * If true, limits selected tags to few rows with vertical scrolling
   * Only applies when displaySelectedOptions is true
   */
  limitDisplayRows?: boolean;

  contentClassName?: string;

  /**
   * Whether a single-select can be cleared
   */
  clearable?: boolean;
}

function ComboboxWithoutRef<
  TValue extends string,
  TMultiselect extends boolean | undefined,
>(
  {
    options: initialOptions,
    value: initialValue,
    state,
    name,
    label,
    onChange,
    multiselect,
    extendOptions,
    search: initialSearch = '',
    onSearchChange,
    clientSearch = true,
    searchPredicate,
    placeholder,
    searchPlaceholder,
    displaySelectedOptions,
    maxSelectedOptions,
    disabled = false,
    noOptionsText,
    loading = false,
    loadMoreDistance = 100,
    onLoadMore,
    limitDisplayRows,
    clearable,
    contentClassName,
  }: ComboboxProps<TValue, TMultiselect>,
  ref: React.Ref<HTMLInputElement>,
) {
  assertMultiSelect({ multiselect, value: initialValue, maxSelectedOptions });
  assertSingleSelect({
    multiselect,
    value: initialValue,
    displaySelectedOptions,
    maxSelectedOptions,
  });

  const [value, setValue] = useState<ComboboxOption<TValue>[]>([]);

  useEffect(() => {
    if (multiselect) {
      setValue((initialValue as ComboboxOption<TValue>[]) ?? []);
    } else {
      setValue(initialValue ? [initialValue as ComboboxOption<TValue>] : []);
    }
  }, [initialValue, multiselect]);

  const options = extendOptions
    ? extendOptionsWithValues(initialOptions, initialValue)
    : initialOptions;

  const {
    options: searchedOptions,
    search,
    setSearch,
  } = useSearch({
    search: initialSearch,
    clientSearch,
    options,
    searchPredicate: searchPredicate ?? SEARCH_BY_LABEL,
    onSearchChange,
    value,
  });

  const onDropdownChange = useCallback(
    (selected: TValue[] | TValue) => {
      const selectedArray = Array.isArray(selected) ? selected : [selected];
      const newVal = selectedArray
        .map((s) => options.find((o) => o.value === s))
        .filter(Boolean) as ComboboxOption<TValue>[];

      if (multiselect) {
        setValue(newVal);
        onChange?.(
          newVal as TMultiselect extends true
            ? ComboboxOption<TValue>[]
            : ComboboxOption<TValue>,
        );
      } else {
        setValue([newVal[0]]);
        onChange?.(
          newVal[0] as TMultiselect extends true
            ? ComboboxOption<TValue>[]
            : ComboboxOption<TValue>,
        );
      }
    },
    [multiselect, options, onChange],
  );

  const dropdownValue = (
    multiselect ? value.map((v) => v.value) : value[0]?.value
  ) as TMultiselect extends true ? TValue[] : TValue;

  return (
    <>
      <Dropdown<TValue, TMultiselect>
        ref={ref}
        filteredOptions={searchedOptions as ComboboxOption<TValue>[]}
        options={options}
        multiselect={multiselect}
        placeholder={placeholder}
        onChange={onDropdownChange}
        value={dropdownValue}
        displaySelectedOptions={displaySelectedOptions}
        name={name}
        label={label}
        state={state}
        maxSelectedOptions={maxSelectedOptions}
        disabled={disabled}
        noOptionsText={noOptionsText}
        loading={loading}
        onClose={() => setSearch('')}
        onLoadMore={onLoadMore}
        loadMoreDistance={loadMoreDistance}
        limitDisplayRows={limitDisplayRows}
        contentClassName={contentClassName}
        clearable={clearable}
      >
        <div
          className={cn(
            'p-md',
            'mb-md',
            'border-emphasis-strong',
            'border-b-px',
          )}
        >
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
          />
        </div>
      </Dropdown>
    </>
  );
}

ComboboxWithoutRef.displayName = 'Combobox';

export const Combobox = forwardRef(ComboboxWithoutRef) as <
  TValue extends string,
  TMultiselect extends boolean | undefined = false,
>(
  props: ComboboxProps<TValue, TMultiselect> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof ComboboxWithoutRef<TValue, TMultiselect>>;
